import { Fragment, useCallback, useMemo, useState } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import {
  bulletsToSqafActionItems,
  bulletsToSqafInsightCards,
  bulletsToSqafRowField,
} from "@/utils/mistralInsightMappers";
import { buildSqafBlockMarkdown } from "@/utils/sqaafMistralInsights";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Download,
  MapPin,
  RefreshCw,
  School,
  TrendingUp,
} from "lucide-react";
import { fmt2 } from "@/lib/sqaafLabels";
import { toTitleCase } from "@/utils/toTitleCase";
import {
  buildBlockActionQueue,
  buildBlockComparisonRows,
  buildBlockHeroCopy,
  buildBlockHeroMetrics,
  buildBlockInsightCards,
  buildHeatmapRows,
  buildRankBarRows,
  buildBlockSummaryRows,
  filterBlocksByBand,
  heatmapCellClass,
  sortBlocks,
} from "@/utils/sqaafBlockDerived";

function SvgIcon({ children, className = "svg-icon" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      {children}
    </svg>
  );
}

export default function SqafBlockPerformance({
  overview,
  blockPerf,
  showSkeleton,
  refreshing,
  onRefresh,
  onExport,
  onDownloadCsv,
  onNavigateTab,
  onBlockClick,
}) {
  const [bandFilter, setBandFilter] = useState("All");
  const [sortKey, setSortKey] = useState("intervention");

  const allBlocks = blockPerf?.block_summary || [];
  const filteredBlocks = useMemo(
    () => sortBlocks(filterBlocksByBand(allBlocks, bandFilter), sortKey),
    [allBlocks, bandFilter, sortKey]
  );

  const sortedByMean = useMemo(
    () => [...allBlocks].sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0)),
    [allBlocks]
  );
  const topBlocks = useMemo(() => sortedByMean.slice(0, 3), [sortedByMean]);
  const bottomBlocks = useMemo(
    () => [...allBlocks].sort((a, b) => (a.mean ?? 0) - (b.mean ?? 0)).slice(0, 5),
    [allBlocks]
  );
  const districtMean = overview?.district_mean ?? 0;

  const hasData = Boolean(allBlocks.length);
  const buildLocalMarkdown = useCallback(
    () => buildSqafBlockMarkdown({ overview, blockPerf }),
    [overview, blockPerf]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ n: allBlocks.length, m: overview?.district_mean, g: overview?.block_gap_display }),
    [allBlocks.length, overview?.district_mean, overview?.block_gap_display]
  );
  const {
    loading: aiLoading,
    pickFromBucket,
    pickHeadline,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "sqaaf-blocks",
    title: "SQAAF Block Performance",
    buildLocalMarkdown,
    context: { overview, blockPerf },
    enabled: hasData,
    scopeKey,
  });

  const localHero = useMemo(() => buildBlockHeroCopy(overview, allBlocks), [overview, allBlocks]);
  const hero = useMemo(() => {
    if (aiLoading) return localHero;
    return {
      ...localHero,
      headline: pickHeadline(localHero.headline),
      body: pickFromBucket(localHero.body, "insights", 0) || localHero.body,
      actionNote: pickFromBucket(localHero.actionNote, "root", 0) || pickPrimaryAction(localHero.actionNote),
      chips: isMistral
        ? [{ label: "Mistral AI Assisted", className: "purple" }, ...localHero.chips]
        : localHero.chips,
    };
  }, [aiLoading, localHero, pickHeadline, pickFromBucket, pickPrimaryAction, isMistral]);

  const heroMetrics = buildBlockHeroMetrics(overview, allBlocks);
  const rankRows = buildRankBarRows(filteredBlocks);
  const { sections, rows: heatmapRows } = buildHeatmapRows(blockPerf, 10);

  const localInsightCards = useMemo(
    () => buildBlockInsightCards(topBlocks, bottomBlocks, overview),
    [topBlocks, bottomBlocks, overview]
  );
  const insightCards = useMemo(
    () => (aiLoading ? localInsightCards : pickMapped(localInsightCards, "insights", bulletsToSqafInsightCards)),
    [aiLoading, pickMapped, localInsightCards]
  );
  const localComparisonRows = useMemo(
    () => buildBlockComparisonRows(allBlocks, districtMean),
    [allBlocks, districtMean]
  );
  const comparisonRows = useMemo(
    () =>
      aiLoading
        ? localComparisonRows
        : pickMapped(localComparisonRows, "root", (bullets, rows) => bulletsToSqafRowField(bullets, rows, "note")) ||
          localComparisonRows,
    [aiLoading, pickMapped, localComparisonRows]
  );
  const localActionQueue = useMemo(
    () => buildBlockActionQueue(bottomBlocks, overview),
    [bottomBlocks, overview]
  );
  const actionQueue = useMemo(
    () =>
      aiLoading
        ? localActionQueue
        : pickMapped(localActionQueue, "actions", bulletsToSqafActionItems) || localActionQueue,
    [aiLoading, pickMapped, localActionQueue]
  );
  const tableRows = buildBlockSummaryRows(filteredBlocks, overview?.completion_rate);

  if (showSkeleton && !overview) {
    return <div className="sq-exec-skeleton" data-testid="sqaf-block-skeleton" />;
  }

  return (
    <>
      <section className="page-header" id="block-performance">
        <div>
          <div className="eyebrow">
            <MapPin className="svg-icon" strokeWidth={2} />
            Block intelligence · Pune District · 2025–26
          </div>
          <h1>Block Performance</h1>
          <p>
            Compare SQAAF score distribution across blocks, identify high-performing clusters, and prioritize blocks
            with weak quality bands or large section gaps.
          </p>
        </div>
        <div className="header-actions">
          <button type="button" className="ghost-action" onClick={onExport}>
            <Download className="svg-icon" strokeWidth={2} />
            Export
          </button>
          <button type="button" className="primary-action" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw className={`svg-icon ${refreshing ? "animate-spin" : ""}`} strokeWidth={2} />
            Refresh
          </button>
        </div>
      </section>

      <section className="scope-card" aria-label="Block performance filters">
        <div className="filter-row">
          <span className="filter-chip">
            District <b>Pune</b>
          </span>
          <span className="filter-chip">
            Academic Year <b>2025–26</b>
          </span>
          <label className="filter-chip">
            Band{" "}
            <b>
              <select
                className="sq-inline-select"
                value={bandFilter}
                onChange={(e) => setBandFilter(e.target.value)}
                aria-label="Filter blocks by band"
              >
                {["All", "Strong", "Developing", "Needs Focus", "Critical"].map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </b>
          </label>
          <label className="filter-chip">
            Sort{" "}
            <b>
              <select
                className="sq-inline-select"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                aria-label="Sort blocks"
              >
                <option value="intervention">Intervention Priority</option>
                <option value="mean">Mean Score</option>
                <option value="schools">School Count</option>
                <option value="critical">Critical %</option>
              </select>
            </b>
          </label>
        </div>
        <div className="scope-note">
          Active: {allBlocks.length} blocks · {Number(overview?.n_schools || 0).toLocaleString()} schools ·{" "}
          {overview?.n_questions || 128} questions
        </div>
      </section>

      <section className="summary-hero">
        <div className="summary-layout">
          <div>
            <div className="score-focus">
              <div className="gauge" style={hero.gaugeStyle}>
                <div>
                  <strong>{hero.districtMean}</strong>
                  <span>District Mean</span>
                </div>
              </div>
              <div className="score-copy">
                <h2 className="dashboard-kpi-hero-title">{toTitleCase(hero.headline)}</h2>
                <p>{hero.body}</p>
                <div className="status-row">
                  {hero.chips.map((chip) => (
                    <span key={chip.label} className={`status-chip ${chip.className}`.trim()}>
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="summary-note">
              <div className="icon-box orange">
                <TrendingUp className="svg-icon" strokeWidth={2} />
              </div>
              <div>
                <b>Executive action signal</b>
                <span>{hero.actionNote}</span>
              </div>
            </div>
          </div>

          <div className="metric-grid">
            {heroMetrics.map((metric) => (
              <article key={metric.label} className="metric-card">
                <div className="metric-head">
                  <small>{metric.label}</small>
                  <span className={`mini-badge ${metric.badge}`.trim()}>
                    {metric.badge === "green" && <CheckCircle2 className="svg-icon" strokeWidth={2} />}
                    {metric.badge === "red" && <AlertTriangle className="svg-icon" strokeWidth={2} />}
                    {metric.badge === "purple" && <BarChart3 className="svg-icon" strokeWidth={2} />}
                    {!metric.badge && <School className="svg-icon" strokeWidth={2} />}
                  </span>
                </div>
                <strong>{metric.value}</strong>
                <span>{metric.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="section-title">
        <div>
          <h2>Block Ranking and Diagnostic Heatmap</h2>
          <p>
            Ranking and heatmap are placed together so users can move from “which block” to “which section” without
            scrolling through multiple raw tables.
          </p>
        </div>
        <a className="section-action" href="#summary-table">
          View full table ↓
        </a>
      </div>

      <section className="analytics-grid">
        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Mean SQAAF Score by Block</h3>
              <p>Ranked highest to lowest. Red bars highlight bottom 5 intervention blocks.</p>
            </div>
            <span className="pill">Top to bottom</span>
          </div>
          <div className="chart-surface">
            <div className="rank-bars" aria-label="Mean SQAAF score by block">
              {rankRows.map((row) => (
                <div key={row.block} className={`rank-row ${row.isLow ? "low" : ""}`}>
                  <span className="block-name">{row.block}</span>
                  <span className="bar-track">
                    <i className="bar-fill" style={{ width: `${row.width}%` }} />
                  </span>
                  <span className="rank-value">{fmt2(row.mean)}</span>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Block × Section Heatmap</h3>
              <p>Darker green means stronger section performance. Blanks show sections with insufficient responses.</p>
            </div>
            <span className="pill">Top 10 sections</span>
          </div>
          <div className="heatmap-wrap">
            <div
              className="heatmap"
              style={{ gridTemplateColumns: `138px 70px repeat(${sections.length}, minmax(54px, 1fr))` }}
            >
              <div className="head block">Block</div>
              <div className="head">Schools</div>
              {sections.map((sec) => (
                <div key={sec} className="head">
                  {sec}
                </div>
              ))}
              {heatmapRows.map((row) => (
                <Fragment key={row.block}>
                  <button type="button" className="block sq-heatmap-block" onClick={() => onBlockClick?.(row.block)}>
                    {row.block}
                  </button>
                  <div>{row.schools}</div>
                  {sections.map((sec) => {
                    const score = row[sec];
                    const cls = heatmapCellClass(score);
                    return (
                      <div key={`${row.block}-${sec}`} className={cls}>
                        {cls === "blank" ? "–" : fmt2(score)}
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        </article>
      </section>

      <section className="insight-grid">
        {insightCards.map((card) => (
          <article key={card.title} className="insight-card">
            <div className={`icon-box ${card.tone}`}>
              {card.tone === "green" && <CheckCircle2 className="svg-icon" strokeWidth={2} />}
              {card.tone === "red" && <AlertTriangle className="svg-icon" strokeWidth={2} />}
              {card.tone === "purple" && <BarChart3 className="svg-icon" strokeWidth={2} />}
            </div>
            <h3>{toTitleCase(card.title)}</h3>
            <p>{card.body}</p>
          </article>
        ))}
      </section>

      <div className="section-title">
        <div>
          <h2>Priority Blocks and Action Queue</h2>
          <p>The lower half converts diagnostics into a review-ready list for block officers and district leadership.</p>
        </div>
        <button type="button" className="section-action" onClick={() => onNavigateTab?.("intervention")}>
          Open policy action →
        </button>
      </div>

      <section className="two-column">
        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Top 5 vs Bottom 5 Blocks</h3>
              <p>Side-by-side ranking makes high performers and immediate risk clusters visible.</p>
            </div>
            <span className="pill">Ranked</span>
          </div>
          <div className="block-list">
            {comparisonRows.map((row) => (
              <div key={`${row.rank}-${row.block}`} className={`block-row ${row.kind === "low" ? "low" : ""}`}>
                <span className="rank">{row.rank}</span>
                <div>
                  <b>{row.block}</b>
                  <span>
                    {row.schools} schools · {row.note}
                  </span>
                </div>
                <div className={`block-score ${row.kind === "top" ? "score-good" : ""}`}>{fmt2(row.mean)}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Three actions mapped to the district review cycle.</p>
            </div>
            <span className="pill">Priority</span>
          </div>
          <div className="action-stack">
            {actionQueue.map((action) => (
              <div key={action.title} className="action-item">
                <div className={`icon-box ${action.tone}`}>
                  {action.tone === "red" && <AlertTriangle className="svg-icon" strokeWidth={2} />}
                  {action.tone === "orange" && <BarChart3 className="svg-icon" strokeWidth={2} />}
                  {action.tone === "green" && <CheckCircle2 className="svg-icon" strokeWidth={2} />}
                </div>
                <div>
                  <h4>{action.title}</h4>
                  <p>{action.body}</p>
                </div>
                <span className={`priority ${action.priorityClass}`}>{action.priority}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="panel table-card" id="summary-table">
        <div className="table-head">
          <div>
            <h3>Block Summary Table</h3>
            <p>Clean table view with band status, critical concentration and completion health.</p>
          </div>
          <div className="table-tools">
            <span className="filter-chip">
              View <b>All blocks</b>
            </span>
            <button type="button" className="ghost-action" onClick={onDownloadCsv}>
              <Download className="svg-icon" strokeWidth={2} />
              Download CSV
            </button>
          </div>
        </div>
        <div className="table-wrap">
          <table aria-label="Block summary table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th>Schools</th>
                <th>Mean</th>
                <th>Median</th>
                <th>Std Dev</th>
                <th>% Critical</th>
                <th>Completion</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.block}>
                  <td>{row.rank}</td>
                  <td>
                    <button type="button" className="sq-table-link" onClick={() => onBlockClick?.(row.block)}>
                      {row.block}
                    </button>
                  </td>
                  <td>{row.schools}</td>
                  <td className={row.isStrong ? "score-good" : row.isWeak ? "score-bad" : ""}>{row.mean}</td>
                  <td>{row.median}</td>
                  <td>{row.std}</td>
                  <td>{row.criticalPct}</td>
                  <td>
                    <div className="progress-mini">
                      <i style={{ width: `${row.completion}%` }} />
                    </div>
                  </td>
                  <td>
                    <span className={`tag ${row.statusTag}`}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
